// Live gameplay-rule schema for the dev/tuning tab.
//
// Each field is a knob the authoritative server accepts (`{t:"rule", k, v}` /
// `{t:"rules", r}` — see `Ruleset::set_rule` in `core/src/weapons.rs`). The
// defaults mirror `Ruleset::warfork()` (the strong fire mode). Start with the
// most relevant knobs; more get added on request.
//
// Match-owned values (`start_health`, `self_damage_scale`, `player_collision`)
// are GameMode fields edited in the mode editor, not rules.

import presetData from "./rules-presets.json";

export interface RuleField {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  def: number;
  /** Optional unit suffix for the readout. */
  unit?: string;
  /**
   * "move" fields must also be applied to the client's local prediction
   * (`session_set_move_rule`) so it matches the server; default is server-only.
   */
  scope?: "move" | "server";
}

export interface RuleGroup {
  title: string;
  fields: RuleField[];
  /** Weapon key prefix (rocket, plasma, …) when this group is a weapon. */
  id?: string;
  /** Inline SVG path for the group-title icon (weapon groups only). */
  icon?: string;
  /** Bind action id for the inline keybind (weapon groups only). */
  bindAction?: string;
}

/** Simple 16×16 line-art paths, one per weapon (Q3 naming). */
export const WEAPON_ICONS: Record<string, string> = {
  rocket: "M8 1 C10 3 11 6 11 9 L8 11 L5 9 C5 6 6 3 8 1 Z M5 10 L4 14 M11 10 L12 14",
  grenade: "M8 5 A5 5 0 1 0 8 15 A5 5 0 1 0 8 5 Z M8 5 L8 2 M6 2 L10 2",
  plasma: "M8 3 A5 5 0 1 0 8 13 A5 5 0 1 0 8 3 Z M8 6 A2 2 0 1 0 8 10 A2 2 0 1 0 8 6 Z",
  lightning: "M9 1 L4 9 L8 9 L7 15 L12 6 L8 6 Z",
  railgun: "M2 8 H14 M8 2 V14 M4 4 L12 12 M12 4 L4 12",
  shotgun: "M2 10 H11 L14 12 M2 10 L1 8 H8 M8 8 V6 H11",
  machinegun: "M2 9 H11 V12 H7 V10 H4 L2 11 Z M11 10 H14",
  melee: "M8 1 L11 10 L8 15 L5 10 Z M8 4 V9",
  experimental: "M6 2 H10 M7 2 V7 L4 12 A2 2 0 0 0 6 15 H10 A2 2 0 0 0 12 12 L9 7 V2",
  ball: "M8 2 A6 6 0 1 0 8 14 A6 6 0 1 0 8 2 Z M8 2 V14 M2 8 H14",
};

interface WeaponDefaults {
  damage: number;
  knockback: number;
  splash: number;
  speed: number;
  reload: number;
  selfDamage: number;
  pellets: number;
  range: number;
  /** Max ammo (dev-tuning; refills the weapon on change). */
  ammo: number;
  /** Ammo granted when spawning with / picking up the weapon. */
  startAmmo: number;
  /** Ammo granted by an ammo box. */
  pickupAmmo: number;
}

function weaponGroup(title: string, name: string, d: WeaponDefaults): RuleGroup {
  return {
    title,
    id: name,
    bindAction: `weapon_${name}`,
    icon: WEAPON_ICONS[name],
    fields: [
      { key: `${name}_damage`, label: "Damage", min: 0, max: 300, step: 1, def: d.damage },
      { key: `${name}_knockback`, label: "Knockback", min: 0, max: 1200, step: 1, def: d.knockback },
      { key: `${name}_splash`, label: "Splash radius", min: 0, max: 400, step: 1, def: d.splash },
      { key: `${name}_speed`, label: "Projectile speed", min: 0, max: 4000, step: 10, def: d.speed },
      { key: `${name}_reload`, label: "Reload", min: 0, max: 3000, step: 10, def: d.reload, unit: "ms" },
      { key: `${name}_self_damage`, label: "Self-damage", min: 0, max: 2, step: 0.05, def: d.selfDamage },
      { key: `${name}_pellets`, label: "Pellets", min: 1, max: 30, step: 1, def: d.pellets },
      { key: `${name}_range`, label: "Range (hitscan)", min: 0, max: 16384, step: 1, def: d.range },
      { key: `${name}_start_ammo`, label: "Start ammo", min: 0, max: 1000, step: 1, def: d.startAmmo },
      { key: `${name}_pickup_ammo`, label: "Pickup ammo", min: 0, max: 1000, step: 1, def: d.pickupAmmo },
      { key: `${name}_ammo`, label: "Max ammo", min: 0, max: 1000, step: 1, def: d.ammo },
    ],
  };
}

function move(
  key: string,
  label: string,
  min: number,
  max: number,
  step: number,
  def: number,
): RuleField {
  return { key, label, min, max, step, def, scope: "move" };
}

/** The experimental weapon: the base weapon knobs plus type/grenade/life/spread. */
function experimentalGroup(): RuleGroup {
  const g = weaponGroup("Experimental (custom)", "experimental", {
    damage: 100,
    knockback: 120,
    splash: 100,
    speed: 1200,
    reload: 800,
    selfDamage: 1,
    pellets: 1,
    range: 0,
    ammo: 50,
    startAmmo: 25,
    pickupAmmo: 25,
  });
  g.fields.push(
    { key: "experimental_spread", label: "Spread (cone)", min: 0, max: 400, step: 1, def: 0 },
    { key: "experimental_kind", label: "Type (0 hitscan · 1 projectile · 2 melee)", min: 0, max: 2, step: 1, def: 1 },
    { key: "experimental_grenade", label: "Grenade (bounce + fuse)", min: 0, max: 1, step: 1, def: 0 },
    { key: "experimental_life", label: "Projectile life", min: 0, max: 10000, step: 50, def: 5000, unit: "ms" },
  );
  return g;
}

export const RULE_GROUPS: RuleGroup[] = [
  {
    title: "Movement / physics",
    fields: [
      move("gravity", "Gravity", 100, 2000, 5, 850),
      move("friction", "Ground friction", 0, 20, 0.5, 8),
      move("stopspeed", "Ground decel / stopspeed", 0, 200, 1, 12),
      move("accelerate", "Ground accel", 0, 50, 0.5, 12),
      move("airaccelerate", "Air accel", 0, 20, 0.1, 1),
      move("airdecelerate", "Air decel", 0, 20, 0.1, 2),
      move("aircontrol", "Air control", 0, 500, 5, 150),
      move("strafebunnyaccel", "Strafe-bunny accel", 0, 300, 1, 70),
      move("wishspeed", "Air wishspeed", 0, 100, 1, 30),
      move("runspeed", "Run speed", 50, 1000, 5, 320),
      move("walkspeed", "Walk speed", 50, 600, 5, 160),
      move("crouchspeed", "Crouch speed", 50, 600, 5, 160),
      move("jumpspeed", "Jump speed", 0, 1000, 5, 280),
      move("dashspeed", "Dash speed", 0, 2000, 10, 450),
      move("dashupspeed", "Dash up-speed", 0, 1000, 5, 174),
      move("dashtime_ms", "Dash delay", 0, 5000, 50, 1000),
      move("wjupspeed", "Walljump up-speed", 0, 1000, 5, 330),
      move("wjbounce", "Walljump bounce", 0, 1, 0.05, 0.3),
      move("wjtime_ms", "Walljump delay", 0, 5000, 50, 1300),
      move("overbounce", "Overbounce", 1, 1.2, 0.005, 1.01),
      move("bunnyaccel", "Forward-bunny accel", 0, 2, 0.01, 0.1593),
      move("bunnytopspeed", "Bunny top speed", 100, 3000, 5, 925),
      move("backtosideratio", "Back-to-side ratio", 0, 1.5, 0.05, 0.8),
      move("airforwardaccel", "Air forward accel", 0, 2, 0.001, 1.00001),
      move("turnaccel", "Turn accel", 0, 20, 0.5, 4),
      move("wateraccelerate", "Water accel", 0, 50, 0.5, 10),
      move("waterfriction", "Water friction", 0, 10, 0.1, 1),
    ],
  },
  {
    title: "Combat",
    fields: [
      { key: "damage_scale", label: "Damage scale", min: 0, max: 5, step: 0.05, def: 1 },
      { key: "knockback_scale", label: "Knockback scale", min: 0, max: 5, step: 0.05, def: 1 },
      { key: "hitbox_scale", label: "Enemy hit volume", min: 1, max: 3, step: 0.1, def: 1.5, unit: "×" },
      { key: "respawn_ms", label: "Respawn", min: 0, max: 10000, step: 50, def: 1000, unit: "ms" },
      { key: "switch_delay_ms", label: "Weapon switch delay", min: 0, max: 2000, step: 10, def: 0, unit: "ms" },
    ],
  },
  weaponGroup("Rocket Launcher", "rocket", { damage: 80, knockback: 100, splash: 125, speed: 1150, reload: 950, selfDamage: 1, pellets: 1, range: 0, ammo: 50, startAmmo: 10, pickupAmmo: 10 }),
  weaponGroup("Grenade Launcher", "grenade", { damage: 80, knockback: 100, splash: 125, speed: 1000, reload: 800, selfDamage: 1, pellets: 1, range: 0, ammo: 50, startAmmo: 10, pickupAmmo: 10 }),
  weaponGroup("Plasma Gun", "plasma", { damage: 15, knockback: 20, splash: 45, speed: 2500, reload: 100, selfDamage: 0.5, pellets: 1, range: 0, ammo: 200, startAmmo: 50, pickupAmmo: 50 }),
  weaponGroup("Lightning Gun / Shaft", "lightning", { damage: 7, knockback: 14, splash: 0, speed: 0, reload: 50, selfDamage: 0, pellets: 1, range: 850, ammo: 250, startAmmo: 100, pickupAmmo: 50 }),
  weaponGroup("Railgun", "railgun", { damage: 75, knockback: 80, splash: 0, speed: 0, reload: 1250, selfDamage: 0, pellets: 1, range: 16384, ammo: 25, startAmmo: 10, pickupAmmo: 10 }),
  weaponGroup("Shotgun", "shotgun", { damage: 5, knockback: 7, splash: 0, speed: 0, reload: 900, selfDamage: 0, pellets: 20, range: 8192, ammo: 50, startAmmo: 10, pickupAmmo: 10 }),
  weaponGroup("Machinegun", "machinegun", { damage: 10, knockback: 10, splash: 0, speed: 0, reload: 100, selfDamage: 0, pellets: 1, range: 6000, ammo: 200, startAmmo: 100, pickupAmmo: 50 }),
  weaponGroup("Melee", "melee", { damage: 60, knockback: 320, splash: 0, speed: 0, reload: 700, selfDamage: 0, pellets: 1, range: 64, ammo: 1, startAmmo: 1, pickupAmmo: 0 }),
  weaponGroup("Ball Launcher", "ball", { damage: 0, knockback: 0, splash: 0, speed: 900, reload: 400, selfDamage: 0, pellets: 1, range: 0, ammo: 999, startAmmo: 999, pickupAmmo: 0 }),
  {
    title: "Balls (physics)",
    fields: [
      { key: "ball_radius", label: "Radius", min: 1, max: 128, step: 1, def: 16 },
      { key: "ball_restitution", label: "Bounciness", min: 0, max: 1.2, step: 0.05, def: 0.6 },
      { key: "ball_friction", label: "Ground friction (roll)", min: 0, max: 1, step: 0.02, def: 0.85 },
      { key: "ball_gravity", label: "Gravity scale", min: 0, max: 3, step: 0.05, def: 1 },
      { key: "ball_push", label: "Player push", min: 0, max: 3, step: 0.05, def: 0.5 },
      { key: "ball_life_ms", label: "Lifetime (0 = forever)", min: 0, max: 120000, step: 100, def: 1200, unit: "ms" },
    ],
  },
  experimentalGroup(),
];

/** The full default rule set (key -> value) = the Warfork preset. */
export function ruleDefaults(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const g of RULE_GROUPS) for (const f of g.fields) out[f.key] = f.def;
  return out;
}

/**
 * Preset overrides live in `rules-presets.json` (shared with the
 * `scripts/weapon-lab.mjs` measurement harness). Values are layered on the
 * Warfork defaults; Quake 3 is source/docs, Quake Live is community-documented
 * and approximate, DeFRaG comes from `Jelvan1/q3df-doc`.
 */

export interface Preset {
  id: string;
  label: string;
  note?: string;
  overrides: Record<string, number>;
}

export const PRESETS: Preset[] = [
  { id: "warfork", label: "Warfork (source-exact)", overrides: presetData.warfork },
  { id: "quake3", label: "Quake 3", overrides: presetData.quake3, note: "Q3 weapon values (GPL source/docs); movement approximated on the Warfork engine." },
  { id: "defrag", label: "Quake 3 DeFRaG (VQ3)", overrides: presetData.defrag, note: "DeFRaG VQ3 physics (gravity 800, friction 6, accel 10, air accel 1, jump 270, overbounce 1.001, no dash/walljump). DeFRaG runs at 125 fps; this engine now runs at 125 Hz too." },
  { id: "defrag-cpm", label: "Quake 3 DeFRaG (CPM)", overrides: presetData["defrag-cpm"], note: "DeFRaG CPM physics (accel 15, air control 150, strafe-bunny 70/30, friction 6, jump 270, overbounce 1.001, no dash/walljump). CPM double-jump adds +100 z (ours adds jump speed); 125 fps caveat." },
  { id: "quakelive", label: "Quake Live", overrides: presetData.quakelive, note: "Community values — QL is closed source, so this is approximate." },
  { id: "bhop", label: "Bhop (Source-style)", overrides: presetData.bhop, note: "Experimental: autohop is already on; this raises air accel toward Source's sv_airaccelerate so you gain speed bhopping. Tune air accel in the Rules tab." },
  { id: "surf", label: "Surf (Source-style)", overrides: presetData.surf, note: "Experimental: high air accel so you can pump speed on steep ramps. Needs surf-ramp geometry — real surf_* maps need the Source importer (docs/research/momentum-surf-bhop.md)." },
  { id: "tricksurf", label: "Tricksurf", overrides: presetData.tricksurf, note: "Surf movement + trick zones (zone system later). Same movement as Surf." },
  { id: "ahop", label: "Ahop (HL2)", overrides: presetData.ahop, note: "Accelerated-hop: autohop with raised air accel (bhop variant)." },
  { id: "conc", label: "Conc (TFC)", overrides: presetData.conc, note: "Concussion-grenade jumping — movement base set; the conc grenade projectile is not implemented yet." },
  { id: "rocketjump", label: "Rocket Jump", overrides: presetData.rocketjump, note: "Full rocket self-damage (rocket_self_damage 1); movement base set for RJ maps." },
  { id: "stickyjump", label: "Sticky Jump", overrides: presetData.stickyjump, note: "Sticky-bomb jumping — needs a sticky projectile (not implemented yet); movement base set." },
  { id: "climb", label: "Climb (KZ)", overrides: presetData.climb, note: "Kreedz: run speed 250 (per Momentum), bhop/surf mechanics; needs climb maps." },
  { id: "parkour", label: "Parkour", overrides: presetData.parkour, note: "Around Titanfall wall-run; we don't have wall-run yet, so this uses walljump/dash for now." },
  { id: "defrag-vintage", label: "Vintage Defrag (Q1-like)", overrides: presetData["defrag-vintage"], note: "Reminiscent of Quake 1 movement (friction 4) with faster pacing; approximate." },
];

/** Dash / walljump enable toggles (buttons in the Rules tab). */
export const DASH_ON: Record<string, number> = { dashspeed: 450, dashupspeed: 174, dashtime_ms: 1000 };
export const DASH_OFF: Record<string, number> = { dashspeed: 0, dashupspeed: 0, dashtime_ms: 0 };
export const WJ_ON: Record<string, number> = { wjupspeed: 330, wjbounce: 0.3 };
export const WJ_OFF: Record<string, number> = { wjupspeed: 0, wjbounce: 0 };

export function dashEnabled(rules: Record<string, number>): boolean {
  return (rules.dashspeed ?? 0) > 0 || (rules.dashupspeed ?? 0) > 0;
}
export function walljumpEnabled(rules: Record<string, number>): boolean {
  return (rules.wjupspeed ?? 0) > 0;
}

/** The full value set for a preset (Warfork defaults + preset overrides). */
export function presetValues(id: string): Record<string, number> {
  const base = ruleDefaults();
  const p = PRESETS.find((x) => x.id === id);
  return { ...base, ...(p ? p.overrides : {}) };
}

export function presetLabel(id: string): string {
  return PRESETS.find((x) => x.id === id)?.label ?? id;
}

export function presetNote(id: string): string {
  return PRESETS.find((x) => x.id === id)?.note ?? "";
}

/** Whether a rule key must also be applied to the client's local prediction. */
/** Clamp/round a value to a field's domain. */
export function clampRule(f: RuleField, v: number): number {
  const clamped = Math.min(f.max, Math.max(f.min, v));
  // Round to the field's step precision to avoid float noise.
  const dp = (String(f.step).split(".")[1] ?? "").length;
  return Number(clamped.toFixed(dp));
}

// ---- Canonical hash (must match `Ruleset::hash` in core/src/weapons.rs) ----

/** All schema keys, sorted (matches the Rust canonical key order). */
export function rulesKeyList(): string[] {
  const keys: string[] = [];
  for (const g of RULE_GROUPS) for (const f of g.fields) keys.push(f.key);
  return keys.sort();
}

/**
 * Canonical `key=<round(value*1e6)>;` string (sorted), excluding the two lossy
 * ms→ticks fields so client and server agree. Must match `Ruleset::dump`.
 */
export function dumpRules(rules: Record<string, number>): string {
  const skip = new Set(["respawn_ms", "switch_delay_ms"]);
  let out = "";
  for (const k of rulesKeyList()) {
    if (skip.has(k)) continue;
    const v = rules[k];
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    out += `${k}=${Math.round(v * 1e6)};`;
  }
  return out;
}

/** FNV-1a 32-bit hash of {@link dumpRules}. Must match `Ruleset::hash`. */
export function hashRules(rules: Record<string, number>): number {
  const s = dumpRules(rules);
  let h = 2166136261 >>> 0;
  for (let j = 0; j < s.length; j++) {
    h = (h ^ s.charCodeAt(j)) >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

// ---- Named profiles (localStorage) ---------------------------------------

const PROFILES_KEY = "webrace.rulesets";

export function loadProfiles(): Record<string, Record<string, number>> {
  try {
    const raw = localStorage.getItem(PROFILES_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

export function saveProfiles(p: Record<string, Record<string, number>>): void {
  try {
    localStorage.setItem(PROFILES_KEY, JSON.stringify(p));
  } catch {
    /* ignore quota */
  }
}
